import { useEffect, useMemo, useRef, useState } from 'react';
import {
  DIR_ORDER, DIR_LABEL, colLabel, drawSpriteFrame, GAME_SPRITE_DRAW_H,
  type Dir8, type FrameOverride, type SpriteSheetCfg,
} from '../../../utils/spriteFrame';

// ─────────────────────────────────────────────────────────────────────────────
// Calibrador de sprite — grade nomeada por posição + editor da posição
// selecionada (grande, ao vivo) + preview "como fica no jogo"
//
// Toda posição (linha × coluna) tem nome (direção + quadro), escala e desvio
// X/Y próprios. Existem DOIS tipos de ajuste, que resolvem problemas
// diferentes:
//   • desvio de DESENHO (offsetX/offsetY) — move o frame já recortado na tela.
//   • desvio de RECORTE (srcOffsetX/srcOffsetY) — move de ONDE, dentro do
//     PNG, o frame é fatiado. É o que resolve cabeça/pé cortado quando o
//     personagem não está alinhado igual em cada linha do sheet (comum em
//     arte gerada por IA) — nenhum ajuste de desenho recupera pixel que ficou
//     fora do recorte.
// `rowOrder` reatribui qual direção mora em cada linha (sheets fora do
// padrão). O preview usa a MESMA rotina do CombatGrid.
// ─────────────────────────────────────────────────────────────────────────────

export interface CalibratorValue {
  rowOrder: string[];                              // [] = ordem padrão
  frameOverrides: Record<string, FrameOverride>;   // "r{lin}c{col}" → override
}

interface Props {
  /** URL da imagem (preview do arquivo escolhido ou /sprites/<filename>). */
  imageUrl: string | null;
  cols: number;
  rows: number;
  /** calibração global — padrão de todas as células. */
  scale: number;
  offsetX: number;
  offsetY: number;
  value: CalibratorValue;
  onChange: (v: CalibratorValue) => void;
  /**
   * Chamado depois de uma troca por arrastar-e-soltar, com o valor já
   * atualizado — o SpriteManagerPage usa isso pra persistir na hora (se o
   * sprite já existe) sem esperar o botão "Guardar Alterações".
   */
  onSwapped?: (v: CalibratorValue) => void;
}

/** Zera chaves que voltaram a bater com o padrão (desenho) ou com 0 (recorte). */
function stripDefaults(o: FrameOverride, globals: { scale: number; offsetX: number; offsetY: number }): FrameOverride {
  const out = { ...o };
  if (out.scale === globals.scale) delete out.scale;
  if (out.offsetX === globals.offsetX) delete out.offsetX;
  if (out.offsetY === globals.offsetY) delete out.offsetY;
  if (out.srcOffsetX === 0) delete out.srcOffsetX;
  if (out.srcOffsetY === 0) delete out.srcOffsetY;
  return out;
}

function useImage(url: string | null) {
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  useEffect(() => {
    if (!url) { setImg(null); return; }
    const i = new Image();
    i.onload = () => setImg(i);
    i.onerror = () => setImg(null);
    i.src = url;
    return () => { i.onload = null; i.onerror = null; };
  }, [url]);
  return img;
}

const num = (v: string, fallback: number) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
};

export default function SpriteCalibrator({
  imageUrl, cols, rows, scale, offsetX, offsetY, value, onChange, onSwapped,
}: Props) {
  const img = useImage(imageUrl);
  const [sel, setSel] = useState<{ r: number; c: number }>({ r: 0, c: 0 });
  const [dragFrom, setDragFrom] = useState<{ r: number; c: number } | null>(null);
  const [dragOver, setDragOver] = useState<{ r: number; c: number } | null>(null);
  const [justSwapped, setJustSwapped] = useState<string[]>([]);

  // mantém a seleção dentro dos limites quando linhas/colunas mudam
  useEffect(() => {
    setSel(s => ({ r: Math.min(s.r, rows - 1), c: Math.min(s.c, cols - 1) }));
  }, [rows, cols]);

  const dirOf = (r: number): Dir8 =>
    (value.rowOrder[r] as Dir8) || DIR_ORDER[Math.min(r, DIR_ORDER.length - 1)];

  const globalCfg: SpriteSheetCfg = useMemo(() => ({
    cols, rows, scale, offsetX, offsetY,
    rowOrder: value.rowOrder.length ? value.rowOrder : null,
    frameOverrides: value.frameOverrides,
  }), [cols, rows, scale, offsetX, offsetY, value]);

  // ── mutations ─────────────────────────────────────────────────────────────
  const setCell = (r: number, c: number, patch: FrameOverride | null) => {
    const key = `r${r}c${c}`;
    const next = { ...value.frameOverrides };
    if (patch === null) {
      delete next[key];
    } else {
      const merged = stripDefaults({ ...next[key], ...patch }, { scale, offsetX, offsetY });
      if (Object.keys(merged).length === 0) delete next[key];
      else next[key] = merged;
    }
    onChange({ ...value, frameOverrides: next });
  };

  /**
   * Arrastar-e-soltar: troca o conteúdo de DUAS posições — cada uma passa a
   * amostrar de onde a outra amostrava no PNG (recorte), levando junto a
   * calibração de desenho (escala/desvio) que pertencia àquele conteúdo.
   * Reflete na hora nos dois previews e, se o sprite já existe, persiste sem
   * precisar do botão "Guardar Alterações".
   */
  const swapCells = (a: { r: number; c: number }, b: { r: number; c: number }) => {
    if (!img || (a.r === b.r && a.c === b.c)) return;
    const frameW = img.width / cols;
    const frameH = img.height / rows;
    const ovA = value.frameOverrides[`r${a.r}c${a.c}`] ?? {};
    const ovB = value.frameOverrides[`r${b.r}c${b.c}`] ?? {};
    // posição absoluta (px do PNG) que cada uma amostra hoje
    const absA = { x: a.c * frameW + (ovA.srcOffsetX ?? 0), y: a.r * frameH + (ovA.srcOffsetY ?? 0) };
    const absB = { x: b.c * frameW + (ovB.srcOffsetX ?? 0), y: b.r * frameH + (ovB.srcOffsetY ?? 0) };
    const newA = stripDefaults(
      { ...ovB, srcOffsetX: Math.round(absB.x - a.c * frameW), srcOffsetY: Math.round(absB.y - a.r * frameH) },
      { scale, offsetX, offsetY },
    );
    const newB = stripDefaults(
      { ...ovA, srcOffsetX: Math.round(absA.x - b.c * frameW), srcOffsetY: Math.round(absA.y - b.r * frameH) },
      { scale, offsetX, offsetY },
    );
    const next = { ...value.frameOverrides };
    const keyA = `r${a.r}c${a.c}`, keyB = `r${b.r}c${b.c}`;
    if (Object.keys(newA).length === 0) delete next[keyA]; else next[keyA] = newA;
    if (Object.keys(newB).length === 0) delete next[keyB]; else next[keyB] = newB;
    const nextValue = { ...value, frameOverrides: next };
    onChange(nextValue);
    onSwapped?.(nextValue);
    setSel(b);
    setJustSwapped([keyA, keyB]);
    setTimeout(() => setJustSwapped(prev => prev.filter(k => k !== keyA && k !== keyB)), 900);
  };

  const applyRow = (r: number, c: number) => {
    const src = value.frameOverrides[`r${r}c${c}`] ?? {};
    const next = { ...value.frameOverrides };
    for (let cc = 0; cc < cols; cc++) {
      if (Object.keys(src).length === 0) delete next[`r${r}c${cc}`];
      else next[`r${r}c${cc}`] = { ...src };
    }
    onChange({ ...value, frameOverrides: next });
  };

  const resetRow = (r: number) => {
    const next = { ...value.frameOverrides };
    for (let c = 0; c < cols; c++) delete next[`r${r}c${c}`];
    onChange({ ...value, frameOverrides: next });
  };

  const setDir = (r: number, dir: Dir8) => {
    const order = [...(value.rowOrder.length ? value.rowOrder : DIR_ORDER.slice(0, rows))];
    while (order.length < rows) order.push(DIR_ORDER[order.length % DIR_ORDER.length]);
    order[r] = dir;
    const isDefault = order.every((d, i) => d === DIR_ORDER[i % DIR_ORDER.length]);
    onChange({ ...value, rowOrder: isDefault ? [] : order.slice(0, rows) });
  };

  const overriddenCount = Object.keys(value.frameOverrides).length;

  if (!imageUrl) {
    return (
      <p style={{ color: 'var(--text-dim)', fontSize: 13 }}>
        Escolha um arquivo (ou edite um sprite existente) para calibrar as posições.
      </p>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 14 }}>Calibração por posição</strong>
        <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>
          {rows}×{cols} = {rows * cols} posições · {overriddenCount} ajustada{overriddenCount === 1 ? '' : 's'} ·
          padrão global: escala {scale} / desvio {String(offsetX)},{String(offsetY)}
        </span>
        {overriddenCount > 0 && (
          <button type="button" className="btn btnSecondary" style={{ padding: '2px 8px', fontSize: 12 }}
            onClick={() => onChange({ ...value, frameOverrides: {} })}>
            Zerar todos os ajustes
          </button>
        )}
      </div>

      {/* ── Editor da posição selecionada — grande, reflete na hora ── */}
      <SelectedEditor
        img={img} cfg={globalCfg} r={sel.r} c={sel.c} dir={dirOf(sel.r)}
        rows={rows} cols={cols}
        onPick={(r, c) => setSel({ r, c })}
        setDir={setDir}
        setCell={setCell}
        applyRow={() => applyRow(sel.r, sel.c)}
        resetCell={() => setCell(sel.r, sel.c, null)}
        scale={scale} offsetX={offsetX} offsetY={offsetY}
      />

      {/* ── Grade nomeada (visão geral / navegação) ── */}
      <div>
        <strong style={{ fontSize: 14 }}>Grade — arraste pra reorganizar</strong>
        <p style={{ fontSize: 12, color: 'var(--text-dim)', margin: '2px 0 8px' }}>
          Clique numa célula pra abrir no editor acima. Arraste uma célula e solte em outra pra{' '}
          <strong>trocar o conteúdo entre as duas</strong> — reflete na hora nos dois previews e,
          se o sprite já existe, já salva sozinho.
        </p>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              <th style={th}>direção da linha</th>
              {Array.from({ length: cols }, (_, c) => (
                <th key={c} style={th}>{colLabel(c)}</th>
              ))}
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rows }, (_, r) => (
              <tr key={r}>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  <select className="formSelect" style={{ fontSize: 12, padding: '2px 4px' }}
                    value={dirOf(r)} onChange={e => setDir(r, e.target.value as Dir8)}>
                    {DIR_ORDER.map(d => <option key={d} value={d}>{DIR_LABEL[d]}</option>)}
                  </select>
                </td>
                {Array.from({ length: cols }, (_, c) => {
                  const key = `r${r}c${c}`;
                  const ov = value.frameOverrides[key];
                  const isSel = sel.r === r && sel.c === c;
                  const isDragSrc = dragFrom?.r === r && dragFrom?.c === c;
                  const isDragOver = dragOver?.r === r && dragOver?.c === c && !isDragSrc;
                  const justDone = justSwapped.includes(key);
                  return (
                    <td key={c} style={{
                      ...td, cursor: img ? 'grab' : 'default',
                      background: justDone ? 'rgba(80,220,140,0.22)'
                        : isDragOver ? 'rgba(120,160,255,0.28)'
                        : isSel ? 'rgba(120,160,255,0.18)'
                        : ov ? 'rgba(255,200,80,0.08)' : undefined,
                      outline: isSel ? '2px solid #6c8cff' : isDragOver ? '2px dashed #6c8cff' : undefined,
                      opacity: isDragSrc ? 0.4 : 1,
                      transition: 'background 0.2s',
                    }}
                      draggable={!!img}
                      onClick={() => setSel({ r, c })}
                      onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', key); setDragFrom({ r, c }); }}
                      onDragEnter={e => { e.preventDefault(); setDragOver({ r, c }); }}
                      onDragOver={e => e.preventDefault()}
                      onDragLeave={() => setDragOver(d => (d?.r === r && d?.c === c ? null : d))}
                      onDrop={e => { e.preventDefault(); if (dragFrom) swapCells(dragFrom, { r, c }); setDragFrom(null); setDragOver(null); }}
                      onDragEnd={() => { setDragFrom(null); setDragOver(null); }}
                      title="Arraste pra outra posição pra trocar o conteúdo entre elas"
                    >
                      <FrameThumb img={img} r={r} c={c} cols={cols} rows={rows}
                        srcOffsetX={ov?.srcOffsetX ?? 0} srcOffsetY={ov?.srcOffsetY ?? 0} />
                      <div style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 3, textAlign: 'center' }}>
                        {justDone ? '↔ trocada' : ov ? '● ajustada' : '—'}
                      </div>
                    </td>
                  );
                })}
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  <button type="button" style={miniBtn} onClick={() => resetRow(r)}>↺ linha</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Como fica no jogo — visão geral das 8 direções ── */}
      <div>
        <strong style={{ fontSize: 14 }}>Como fica no jogo — visão geral</strong>
        <p style={{ fontSize: 12, color: 'var(--text-dim)', margin: '2px 0 8px' }}>
          Cada direção com a linha-de-chão, alternando parado ↔ passada — no tamanho real da batalha.
          Clique numa direção pra abrir no editor acima.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {DIR_ORDER.map((d, i) => (
            <div key={d} onClick={() => {
              const row = value.rowOrder.length ? value.rowOrder.indexOf(d) : i;
              setSel({ r: row >= 0 ? row : i, c: sel.c });
            }} style={{ cursor: 'pointer' }}>
              <GamePreview img={img} cfg={globalCfg} dirIndex={i} label={DIR_LABEL[d].split(' ')[0]} animate />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── editor grande da posição selecionada ────────────────────────────────────
function SelectedEditor({
  img, cfg, r, c, dir, rows, cols, onPick, setDir, setCell, applyRow, resetCell, scale, offsetX, offsetY,
}: {
  img: HTMLImageElement | null; cfg: SpriteSheetCfg; r: number; c: number; dir: Dir8;
  rows: number; cols: number;
  onPick: (r: number, c: number) => void;
  setDir: (r: number, d: Dir8) => void;
  setCell: (r: number, c: number, patch: FrameOverride | null) => void;
  applyRow: () => void;
  resetCell: () => void;
  scale: number; offsetX: number; offsetY: number;
}) {
  const key = `r${r}c${c}`;
  const ov = cfg.frameOverrides?.[key];
  const eff = {
    scale: ov?.scale ?? scale,
    offsetX: ov?.offsetX ?? offsetX,
    offsetY: ov?.offsetY ?? offsetY,
    srcOffsetX: ov?.srcOffsetX ?? 0,
    srcOffsetY: ov?.srcOffsetY ?? 0,
  };
  const isOverridden = !!ov;

  return (
    <div style={{ border: '1px solid var(--accent)', borderRadius: 8, padding: 14, background: 'rgba(120,160,255,0.05)' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 10 }}>
        <strong style={{ fontSize: 13 }}>Editando posição:</strong>
        <select className="formSelect" style={{ fontSize: 12 }} value={r}
          onChange={e => onPick(Number(e.target.value), c)}>
          {Array.from({ length: rows }, (_, rr) => (
            <option key={rr} value={rr}>linha {rr} — {DIR_LABEL[(cfg.rowOrder?.[rr] as Dir8) || DIR_ORDER[rr % DIR_ORDER.length]]}</option>
          ))}
        </select>
        <select className="formSelect" style={{ fontSize: 12 }} value={c}
          onChange={e => onPick(r, Number(e.target.value))}>
          {Array.from({ length: cols }, (_, cc) => (
            <option key={cc} value={cc}>{colLabel(cc)}</option>
          ))}
        </select>
        <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>
          direção desta linha:
        </span>
        <select className="formSelect" style={{ fontSize: 12 }} value={dir}
          onChange={e => setDir(r, e.target.value as Dir8)}>
          {DIR_ORDER.map(d => <option key={d} value={d}>{DIR_LABEL[d]}</option>)}
        </select>
        {isOverridden && <span style={{ fontSize: 11, color: '#ffc850' }}>● ajustada</span>}
      </div>

      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <RecorteBig img={img} cfg={cfg} r={r} c={c} />
          <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>recorte (o que é fatiado do PNG)</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <GamePreviewStatic img={img} cfg={cfg} r={r} c={c} />
          <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>como fica no jogo</span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: '6px 14px', alignContent: 'start' }}>
          <span style={{ fontSize: 11, color: 'var(--text-dim)', gridColumn: '1 / -1', fontWeight: 600 }}>Desenho (onde aparece na tela)</span>
          <label style={lbl}>escala
            <input type="number" step={0.05} style={inp} value={eff.scale}
              onChange={e => setCell(r, c, { scale: num(e.target.value, scale) })} />
          </label>
          <label style={lbl}>desvio X
            <input type="number" style={inp} value={String(eff.offsetX)}
              onChange={e => setCell(r, c, { offsetX: Math.round(num(e.target.value, offsetX)) })} />
          </label>
          <label style={lbl}>desvio Y
            <input type="number" style={inp} value={String(eff.offsetY)}
              onChange={e => setCell(r, c, { offsetY: Math.round(num(e.target.value, offsetY)) })} />
          </label>

          <span style={{ fontSize: 11, color: 'var(--text-dim)', gridColumn: '1 / -1', fontWeight: 600, marginTop: 6 }}>
            Recorte (de onde no PNG é fatiado — resolve cabeça/pé cortado)
          </span>
          <label style={lbl}>recorte X
            <input type="number" style={inp} value={String(eff.srcOffsetX)}
              onChange={e => setCell(r, c, { srcOffsetX: Math.round(num(e.target.value, 0)) })} />
          </label>
          <label style={lbl}>recorte Y
            <input type="number" style={inp} value={String(eff.srcOffsetY)}
              onChange={e => setCell(r, c, { srcOffsetY: Math.round(num(e.target.value, 0)) })} />
          </label>

          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, marginTop: 8 }}>
            <button type="button" className="btn btnSecondary" style={{ fontSize: 12, padding: '3px 10px' }}
              onClick={applyRow}>aplicar à linha inteira</button>
            <button type="button" className="btn btnSecondary" style={{ fontSize: 12, padding: '3px 10px' }}
              disabled={!isOverridden} onClick={resetCell}>↺ voltar ao padrão</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── recorte estático de um frame (miniatura da grade) ───────────────────────
function FrameThumb({ img, r, c, cols, rows, srcOffsetX = 0, srcOffsetY = 0 }: {
  img: HTMLImageElement | null; r: number; c: number; cols: number; rows: number;
  srcOffsetX?: number; srcOffsetY?: number;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  const S = 44;
  useEffect(() => {
    const cv = ref.current; if (!cv) return;
    const ctx = cv.getContext('2d'); if (!ctx) return;
    ctx.clearRect(0, 0, S, S);
    if (!img) return;
    const fw = img.width / cols, fh = img.height / rows;
    const sx = Math.max(0, Math.min(img.width - fw, c * fw + srcOffsetX));
    const sy = Math.max(0, Math.min(img.height - fh, r * fh + srcOffsetY));
    const k = Math.min(S / fw, S / fh);
    const w = fw * k, h = fh * k;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(img, sx, sy, fw, fh, (S - w) / 2, (S - h) / 2, w, h);
  }, [img, r, c, cols, rows, srcOffsetX, srcOffsetY]);
  return <canvas ref={ref} width={S} height={S} style={{ border: '1px solid var(--border)', borderRadius: 4, background: 'var(--input-bg)', display: 'block', margin: '0 auto' }} />;
}

// ── recorte GRANDE da posição selecionada, com guia de bordas ──────────────
function RecorteBig({ img, cfg, r, c }: { img: HTMLImageElement | null; cfg: SpriteSheetCfg; r: number; c: number }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const S = 160;
  useEffect(() => {
    const cv = ref.current; if (!cv) return;
    const ctx = cv.getContext('2d'); if (!ctx) return;
    ctx.clearRect(0, 0, S, S);
    if (!img) return;
    const cols = Math.max(1, cfg.cols), rows = Math.max(1, cfg.rows);
    const fw = img.width / cols, fh = img.height / rows;
    const ov = cfg.frameOverrides?.[`r${r}c${c}`];
    const sx = Math.max(0, Math.min(img.width - fw, c * fw + (ov?.srcOffsetX ?? 0)));
    const sy = Math.max(0, Math.min(img.height - fh, r * fh + (ov?.srcOffsetY ?? 0)));
    const k = Math.min(S / fw, S / fh);
    const w = fw * k, h = fh * k;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(img, sx, sy, fw, fh, (S - w) / 2, (S - h) / 2, w, h);
    // guia da borda do recorte
    ctx.strokeStyle = 'rgba(108,140,255,0.6)';
    ctx.lineWidth = 1;
    ctx.strokeRect((S - w) / 2 + 0.5, (S - h) / 2 + 0.5, w - 1, h - 1);
  }, [img, cfg, r, c]);
  return <canvas ref={ref} width={S} height={S} style={{ border: '1px solid var(--border)', borderRadius: 6, background: 'var(--input-bg)' }} />;
}

// ── preview estático (1 frame, sem animação) idêntico ao jogo ──────────────
function GamePreviewStatic({ img, cfg, r, c }: { img: HTMLImageElement | null; cfg: SpriteSheetCfg; r: number; c: number }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const W = 160, H = 170;
  useEffect(() => {
    const cv = ref.current; if (!cv) return;
    const ctx = cv.getContext('2d'); if (!ctx) return;
    ctx.clearRect(0, 0, W, H);
    const groundY = H - 20, groundX = W / 2;
    ctx.strokeStyle = 'rgba(255,255,255,0.15)';
    ctx.beginPath(); ctx.moveTo(8, groundY); ctx.lineTo(W - 8, groundY); ctx.stroke();
    ctx.beginPath(); ctx.ellipse(groundX, groundY + 3, 22, 6, 0, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0,0,0,0.25)'; ctx.fill();
    if (!img) return;
    const dirIndex = DIR_ORDER.indexOf((cfg.rowOrder?.[r] as Dir8) || DIR_ORDER[r % DIR_ORDER.length]);
    ctx.imageSmoothingEnabled = false;
    drawSpriteFrame(ctx, img, cfg, { dirIndex: dirIndex >= 0 ? dirIndex : r, col: c, groundX, groundY, baseDrawH: GAME_SPRITE_DRAW_H * 1.6 });
  }, [img, cfg, r, c]);
  return <canvas ref={ref} width={W} height={H} style={{ border: '1px solid var(--border)', borderRadius: 6, background: 'var(--input-bg)' }} />;
}

// ── preview animado (galeria das 8 direções) idêntico ao jogo ──────────────
function GamePreview({ img, cfg, dirIndex, label, animate }: {
  img: HTMLImageElement | null; cfg: SpriteSheetCfg; dirIndex: number; label: string; animate?: boolean;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  const W = 96, H = 110;
  useEffect(() => {
    const cv = ref.current; if (!cv) return;
    const ctx = cv.getContext('2d'); if (!ctx) return;
    let raf = 0;
    const groundY = H - 14, groundX = W / 2;
    const walkSeq = cfg.cols >= 3 ? [0, 1, 0, 2] : cfg.cols === 2 ? [0, 1] : [0];
    const draw = (t: number) => {
      ctx.clearRect(0, 0, W, H);
      ctx.strokeStyle = 'rgba(255,255,255,0.15)';
      ctx.beginPath(); ctx.moveTo(6, groundY); ctx.lineTo(W - 6, groundY); ctx.stroke();
      ctx.beginPath(); ctx.ellipse(groundX, groundY + 2, 16, 5, 0, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(0,0,0,0.25)'; ctx.fill();
      if (img) {
        const col = animate ? walkSeq[Math.floor(t / 220) % walkSeq.length] : 0;
        const bob = col === 0 ? 0 : Math.abs(Math.sin(t / 110)) * 3;
        ctx.imageSmoothingEnabled = false;
        drawSpriteFrame(ctx, img, cfg, { dirIndex, col, groundX, groundY, bob, baseDrawH: GAME_SPRITE_DRAW_H });
      }
      if (animate) raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [img, cfg, dirIndex, animate]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <canvas ref={ref} width={W} height={H} style={{ border: '1px solid var(--border)', borderRadius: 6, background: 'var(--input-bg)' }} />
      <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>{label}</span>
    </div>
  );
}

const th: React.CSSProperties = { padding: '4px 6px', textAlign: 'left', borderBottom: '1px solid var(--border)', color: 'var(--text-dim)', fontWeight: 600 };
const td: React.CSSProperties = { padding: '5px 6px', borderBottom: '1px solid var(--border)', verticalAlign: 'top', borderRadius: 4 };
const lbl: React.CSSProperties = { display: 'flex', flexDirection: 'column', fontSize: 10, color: 'var(--text-dim)', gap: 1 };
const inp: React.CSSProperties = { width: 70, padding: '2px 5px', fontSize: 12, background: 'var(--input-bg)', border: '1px solid var(--border)', borderRadius: 3, color: 'var(--text)' };
const miniBtn: React.CSSProperties = { display: 'block', padding: '1px 6px', fontSize: 10, background: 'transparent', border: '1px solid var(--border)', borderRadius: 3, color: 'var(--accent)', cursor: 'pointer' };
